/* ============================================================
   androidAdb.js — Browser WebUSB + ADB wrapper (Tango / ya-webadb)
   ============================================================
   FASA 1 (POC, ADMIN SAHAJA) — rujuk page AndroidAutoView.jsx.

   · SEMUA operasi USB/ADB berlaku DI BROWSER (local). Tiada data
     device dihantar ke server. Tiada Firestore. Tiada backend.
   · Library ya-webadb dimuat secara DINAMIK (import() di dalam
     loadLibs) — jadi kod berat ini hanya di-fetch bila admin benar2
     tekan "Connect". Bundle pengguna biasa tidak terkesan.
   · Versi dipasang: @yume-chan/adb 2.6.4 · adb-daemon-webusb 2.3.2 ·
     adb-credential-web 2.1.0 (semua tag `latest` stabil — BUKAN beta).

   Aliran sambungan (disahkan dari .d.ts pakej yang dipasang):
     manager = AdbDaemonWebUsbDeviceManager.BROWSER
     device  = await manager.requestDevice({ filters:[ADB_FILTER] })
     conn    = await device.connect()
     tport   = await AdbDaemonTransport.authenticate({ serial, connection, credentialStore })
     adb     = new Adb(tport)
   Shell : adb.subprocess.noneProtocol.spawnWaitText(cmd)
   Prop  : adb.getProp(key)
   Screen: adb.framebuffer() -> { width, height, data(RGBA) }
   Close : adb.close()
   ============================================================ */

// Deskriptor interface ADB rasmi — bantu Chrome tapis senarai peranti
// supaya hanya telefon ber-USB-Debugging muncul.
const ADB_FILTER = { classCode: 0xff, subclassCode: 0x42, protocolCode: 1 };

// Ralat bertanda kod — supaya UI boleh papar mesej jelas (bukan fake success).
export class AdbError extends Error {
  constructor(code, message) {
    super(message);
    this.name = 'AdbError';
    this.code = code;
  }
}

export function isWebUsbSupported() {
  return typeof navigator !== 'undefined' && !!navigator.usb;
}

// Serial telefon terakhir yang disambung — supaya "Reconnect" pilih balik
// telefon yang sama tanpa paparan pemilih Chrome.
const LAST_SERIAL_KEY = 'aat_last_serial';

// Senarai telefon yang PERNAH dibenarkan dalam Chrome (tiada popup pemilih).
// Dipakai page untuk tunjuk butang "Reconnect".
export async function listGrantedDevices() {
  if (!isWebUsbSupported()) return [];
  const { AdbDaemonWebUsbDeviceManager } = await loadLibs();
  const manager = AdbDaemonWebUsbDeviceManager.BROWSER;
  if (!manager) return [];
  try {
    const devices = await manager.getDevices();
    return devices.map((d) => ({ serial: d.serial, name: d.name }));
  } catch {
    return [];
  }
}

// Parse XML uiautomator → senarai elemen (selector + bounds). Dikongsi
// page (inspector) + enjin replay (Fasa 5). DOMParser = global browser.
// Fail video card di telefon. Nama ikut card (bukan nama produk sahaja): dua card nama
// serupa dipotong jadi nama fail sama dan menimpa video satu sama lain.
export function phoneVideoDest(name, cardId, setKey) {
  const safe = String(name || 'video').replace(/[^a-zA-Z0-9._-]+/g, '_').slice(0, 40);
  const id = String(cardId || '').replace(/[^a-zA-Z0-9]/g, '').slice(-8);
  return `/sdcard/DCIM/SESAAT_${safe}_${id}_${setKey}.mp4`;
}

// Arahan ke phone boleh tergantung tanpa ralat (ukur 1 Nov: bacaan skrin beku > 2 minit).
// Larian tanpa pengawasan mesti gagal jelas, bukan menunggu selamanya.
function tamatDalam(kerja, ms, label) {
  return new Promise((ok, no) => {
    const t = setTimeout(() => no(new AdbError('PHONE_TIMEOUT', `${label}: telefon tiada jawapan selepas ${ms / 1000} saat.`)), ms);
    kerja.then((v) => { clearTimeout(t); ok(v); }, (e) => { clearTimeout(t); no(e); });
  });
}

// Butang Release phone: lepaskan USB dari tab ini supaya browser lain boleh Connect. Larian
// yang sedang jalan akan terhenti. Tab/browser lain tak boleh dilepaskan dari sini (had
// WebUSB). Pulang bilangan telefon yang dilepaskan.
export async function lepaskanTelefon() {
  if (!navigator.usb) return 0;
  let n = 0;
  for (const d of await navigator.usb.getDevices()) {
    if (d.opened) { await d.close(); n++; }
  }
  return n;
}

export function parseUiXml(xml) {
  const docx = new DOMParser().parseFromString(xml, 'text/xml');
  const nodes = Array.from(docx.getElementsByTagName('node'));
  return nodes.map((n, i) => {
    const b = n.getAttribute('bounds') || '';
    const m = b.match(/\[(\d+),(\d+)\]\[(\d+),(\d+)\]/);
    const bounds = m ? { x1: +m[1], y1: +m[2], x2: +m[3], y2: +m[4] } : null;
    return {
      i,
      text: n.getAttribute('text') || '',
      desc: n.getAttribute('content-desc') || '',
      id: n.getAttribute('resource-id') || '',
      cls: (n.getAttribute('class') || '').split('.').pop(),
      clickable: n.getAttribute('clickable') === 'true',
      checked: n.getAttribute('checked') === 'true', // untuk toggle/switch (cth AI label)
      bounds,
    };
  });
}

let _libs = null;
async function loadLibs() {
  if (_libs) return _libs;
  const [adbMod, webusbMod, credMod] = await Promise.all([
    import('@yume-chan/adb'),
    import('@yume-chan/adb-daemon-webusb'),
    import('@yume-chan/adb-credential-web'),
  ]);
  _libs = {
    Adb: adbMod.Adb,
    AdbDaemonTransport: adbMod.AdbDaemonTransport,
    AdbDaemonWebUsbDeviceManager: webusbMod.AdbDaemonWebUsbDeviceManager,
    AdbWebCredentialStore: credMod.default,
  };
  return _libs;
}

export class AndroidController {
  constructor(onLog, onDisconnect) {
    this.onLog = typeof onLog === 'function' ? onLog : () => {};
    this.onDisconnect = typeof onDisconnect === 'function' ? onDisconnect : () => {};
    this.device = null;
    this.transport = null;
    this.adb = null;
    this.info = null;
    this._closed = false;
  }

  log(msg, level = 'info') {
    this.onLog({ ts: Date.now(), level, msg });
  }

  get connected() {
    return !!this.adb && !this._closed;
  }

  // Connect BARU — buka pemilih Chrome untuk user pilih telefon.
  async connect() {
    const { AdbDaemonWebUsbDeviceManager } = await this._manager();
    this.log('Membuka pemilih peranti USB — pilih telefon anda dalam senarai Chrome…');
    let device;
    try {
      device = await AdbDaemonWebUsbDeviceManager.BROWSER.requestDevice({ filters: [ADB_FILTER] });
    } catch (e) {
      throw new AdbError('REQUEST_FAILED', 'Tidak dapat membuka pemilih USB: ' + (e?.message || e));
    }
    if (!device) {
      throw new AdbError('NO_DEVICE',
        'Tiada peranti dipilih. Pastikan kabel USB disambung, USB Debugging ON, lalu pilih telefon dalam senarai. Jika telefon tak muncul: tukar mod USB ke "File Transfer / MTP" dan cuba semula.');
    }
    return this._attach(device, false);
  }

  // Reconnect — sambung semula ke telefon yang PERNAH dibenarkan (tiada pemilih,
  // tiada "Allow" lagi di telefon kerana kunci authorization sudah disimpan).
  async reconnect(preferredSerial) {
    const { AdbDaemonWebUsbDeviceManager } = await this._manager();
    const devices = await AdbDaemonWebUsbDeviceManager.BROWSER.getDevices();
    if (!devices.length) {
      throw new AdbError('NO_GRANTED',
        'Tiada telefon yang pernah dibenarkan dalam Chrome. Tekan "Connect USB" untuk pilih telefon.');
    }
    let want = preferredSerial;
    if (!want) { try { want = localStorage.getItem(LAST_SERIAL_KEY) || ''; } catch { want = ''; } }
    const device = (want && devices.find((d) => d.serial === want)) || devices[0];
    this.log(`Menyambung semula ke telefon dibenarkan: ${device.name || 'Android'} (serial ${device.serial || '—'})…`);
    return this._attach(device, true);
  }

  async _manager() {
    if (!isWebUsbSupported()) {
      throw new AdbError('WEBUSB_UNSUPPORTED',
        'Browser ini tidak menyokong WebUSB. Guna Google Chrome (desktop) di Windows.');
    }
    const libs = await loadLibs();
    if (!libs.AdbDaemonWebUsbDeviceManager.BROWSER) {
      throw new AdbError('WEBUSB_UNSUPPORTED', 'WebUSB tidak tersedia dalam browser ini. Guna Google Chrome (desktop).');
    }
    return libs;
  }

  // Aliran bersama connect + reconnect: buka socket USB → authorize → cipta Adb.
  async _attach(device, silent) {
    const { Adb, AdbDaemonTransport, AdbWebCredentialStore } = await loadLibs();
    this.device = device;

    let connection;
    try {
      connection = await device.connect();
    } catch (e) {
      const m = String(e?.message || e);
      // Pelbagai ayat bila port USB telefon dipegang program/tab lain (Windows/Chrome):
      // "already in used by another program", "Unable to claim interface", "Access denied",
      // "Failed to open the device"… — padan luas supaya mesej jelas muncul, bukan CONNECT_FAILED generik.
      if (/claim|busy|access|in use|in used|another program|another app|open the device|cannot open|unable to open|failed to open|already open|InterfaceClaim|SecurityError|NetworkError/i.test(m)) {
        throw new AdbError('INTERFACE_BUSY',
          'Telefon ini sedang digunakan oleh program atau tab lain di komputer anda. Cara betulkan: (1) Cabut kabel USB telefon, tunggu 3 saat, kemudian cucuk semula. (2) Tutup tab browser atau program lain yang mungkin sedang guna telefon. (3) Tekan Connect sekali lagi.');
      }
      throw new AdbError('CONNECT_FAILED', 'Gagal menyambung ke peranti: ' + m);
    }

    if (!silent) {
      this.log('Menunggu kebenaran DI TELEFON — tekan "Allow / Benarkan" pada popup "Allow USB debugging?" (tanda "Always allow from this computer")…', 'warn');
    }
    const credentialStore = new AdbWebCredentialStore('SESAAT Android Test');
    try {
      this.transport = await AdbDaemonTransport.authenticate({ serial: device.serial, connection, credentialStore });
    } catch (e) {
      throw new AdbError('AUTH_FAILED',
        'Authorization ADB gagal. Pastikan anda tekan "Allow USB debugging" di telefon, kemudian cuba semula. (' + (e?.message || e) + ')');
    }

    this.adb = new Adb(this.transport);
    this._closed = false;
    try { localStorage.setItem(LAST_SERIAL_KEY, device.serial || ''); } catch { /* abaikan */ }
    this.log('Tersambung + authorized ✔', 'ok');

    // Kesan cabut USB / transport tutup
    this.adb.disconnected
      .then(() => {
        if (!this._closed) {
          this._closed = true;
          this.log('Sambungan USB terputus.', 'warn');
          this.onDisconnect();
        }
      })
      .catch(() => {});

    return true;
  }

  _ensure() {
    if (!this.connected) {
      throw new AdbError('NOT_CONNECTED', 'Belum tersambung ke telefon. Tekan Connect dahulu.');
    }
  }

  async getDeviceInfo() {
    this._ensure();
    const get = async (k) => {
      try { return (await this.adb.getProp(k) || '').trim(); } catch { return ''; }
    };
    const [model, manufacturer, brand, release, sdk] = await Promise.all([
      get('ro.product.model'),
      get('ro.product.manufacturer'),
      get('ro.product.brand'),
      get('ro.build.version.release'),
      get('ro.build.version.sdk'),
    ]);

    let screenRaw = '';
    try {
      screenRaw = (await this.adb.subprocess.noneProtocol.spawnWaitText('wm size')).trim();
    } catch { /* biar kosong */ }
    const m = screenRaw.match(/(\d+)\s*x\s*(\d+)/);

    const info = {
      serial: this.device?.serial || '',
      usbName: this.device?.name || '',
      model,
      manufacturer,
      brand,
      androidVersion: release,
      sdk,
      screenRaw,
      screenWidth: m ? Number(m[1]) : null,
      screenHeight: m ? Number(m[2]) : null,
    };
    this.info = info;
    this.log(`Device auto-detect: ${(manufacturer || brand || '').trim()} ${model || ''} · Android ${release || '?'} (SDK ${sdk || '?'}) · skrin ${screenRaw || 'tidak diketahui'}`);
    return info;
  }

  async screenshot() {
    this._ensure();
    let fb;
    try {
      fb = await tamatDalam(this.adb.framebuffer(), 25000, 'Screenshot');
    } catch (e) {
      const txt = `${e?.name || ''} ${e?.message || e}`;
      if (/forbidden/i.test(txt)) {
        throw new AdbError('SCREENSHOT_FORBIDDEN',
          'Skrin ini tidak boleh di-screenshot. Sesetengah app (contoh skrin sensitif) menghalang screenshot (FLAG_SECURE) — ini sekatan Android, bukan bug.');
      }
      if (/unsupported/i.test(txt)) {
        throw new AdbError('SCREENSHOT_UNSUPPORTED',
          'Format framebuffer peranti ini tidak disokong oleh library. Sila hantar log kepada admin.');
      }
      throw new AdbError('SCREENSHOT_FAILED', 'Gagal mengambil screenshot: ' + (e?.message || e));
    }

    const { width, height, data } = fb;
    const expected = width * height * 4;
    if (!data || data.length !== expected) {
      throw new AdbError('SCREENSHOT_FORMAT',
        `Saiz framebuffer tidak dijangka (${data ? data.length : 0} bait, dijangka ${expected}). Peranti mungkin guna format warna lain.`);
    }

    const px = new Uint8ClampedArray(data); // salinan RGBA
    // Framebuffer biasanya RGBA8888; jika BGRA (red_offset 16), tukar R<->B.
    if (fb.red_offset === 16 && fb.blue_offset === 0) {
      for (let i = 0; i < px.length; i += 4) {
        const r = px[i];
        px[i] = px[i + 2];
        px[i + 2] = r;
      }
    }

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    ctx.putImageData(new ImageData(px, width, height), 0, 0);
    const dataUrl = canvas.toDataURL('image/png');

    this.log(`Screenshot diambil: ${width}×${height}`);
    return { width, height, dataUrl };
  }

  async _run(argv, label) {
    this._ensure();
    let out;
    try {
      out = (await tamatDalam(this.adb.subprocess.noneProtocol.spawnWaitText(argv.map(String)), 30000, label)).trim();
    } catch (e) {
      throw new AdbError('SHELL_FAILED', `${label} gagal: ${e?.message || e}`);
    }
    // Protokol "none" tiada exit-code. `input` yang berjaya TIDAK mencetak apa-apa.
    // Jadi: output kosong = kemungkinan besar OK; output ada = kemungkinan ralat → amaran, BUKAN ✔.
    if (out) this.log(`${label} — telefon menjawab: ${out}`, 'warn');
    else this.log(`${label} dihantar (tiada ralat dilaporkan)`, 'ok');
    return out;
  }

  tap(x, y) {
    const ix = Math.round(x), iy = Math.round(y);
    return this._run(['input', 'tap', ix, iy], `Tap (${ix}, ${iy})`);
  }

  swipe(x1, y1, x2, y2, ms = 300) {
    const a = Math.round(x1), b = Math.round(y1), c = Math.round(x2), d = Math.round(y2), t = Math.round(ms);
    return this._run(['input', 'swipe', a, b, c, d, t], `Swipe (${a},${b})→(${c},${d}) ${t}ms`);
  }

  home() { return this._run(['input', 'keyevent', '3'], 'Home (KEYCODE_HOME)'); }
  back() { return this._run(['input', 'keyevent', '4'], 'Back (KEYCODE_BACK)'); }

  // ── Generik (app-agnostic) — enjin automation universal ──

  // Senarai package app. includeSystem=false → app dipasang-user sahaja (pm -3).
  // Pulang array nama package (tiada hardcode app tertentu).
  async listApps(includeSystem = false) {
    this._ensure();
    const argv = includeSystem
      ? ['pm', 'list', 'packages']
      : ['pm', 'list', 'packages', '-3'];
    let out;
    try {
      out = await this.adb.subprocess.noneProtocol.spawnWaitText(argv);
    } catch (e) {
      throw new AdbError('LIST_APPS_FAILED', 'Gagal mendapatkan senarai app: ' + (e?.message || e));
    }
    const pkgs = out.split(/\r?\n/)
      .map((l) => l.trim())
      .filter((l) => l.startsWith('package:'))
      .map((l) => l.slice('package:'.length).trim())
      .filter(Boolean)
      .sort();
    this.log(`Senarai app: ${pkgs.length} package (${includeSystem ? 'termasuk sistem' : 'dipasang-user'})`);
    return pkgs;
  }

  // Buka app ikut nama package (tanpa perlu tahu main activity) guna monkey LAUNCHER.
  async launchApp(packageName) {
    this._ensure();
    if (!packageName) throw new AdbError('NO_PACKAGE', 'Tiada package dipilih.');
    let out;
    try {
      out = (await this.adb.subprocess.noneProtocol.spawnWaitText(
        ['monkey', '-p', packageName, '-c', 'android.intent.category.LAUNCHER', '1'],
      )).trim();
    } catch (e) {
      throw new AdbError('LAUNCH_FAILED', `Gagal membuka ${packageName}: ${e?.message || e}`);
    }
    if (/No activities found|monkey aborted|Error:|Exception/i.test(out)) {
      const first = out.split(/\r?\n/)[0] || '';
      throw new AdbError('LAUNCH_FAILED', `Tidak dapat membuka ${packageName} — app ini mungkin tiada skrin utama yang boleh dilancarkan. (${first})`);
    }
    this.log(`Launch ${packageName} ✔`, 'ok');
    return true;
  }

  // Package app yang sedang DI DEPAN skrin (foreground). Guna dumpsys —
  // jalan walaupun skrin beranimasi (tak macam uiautomator). '' = tak dapat
  // baca (jangan anggap gagal). Dipakai enjin: pastikan masih dalam app betul.
  async getForegroundPackage() {
    this._ensure();
    let out = '';
    try {
      out = await tamatDalam(this.adb.subprocess.noneProtocol.spawnWaitText(['dumpsys', 'window']), 15000, 'Baca app depan');
    } catch {
      try { out = await tamatDalam(this.adb.subprocess.noneProtocol.spawnWaitText(['dumpsys', 'activity', 'activities']), 15000, 'Baca app depan'); }
      catch { return ''; }
    }
    // mCurrentFocus=Window{hash u0 com.shopee.my/com.shopee....Activity}
    let m = out.match(/mCurrentFocus=Window\{[^}]*\s([a-zA-Z0-9_.]+)\//);
    if (!m) m = out.match(/mFocusedApp=\S*\s([a-zA-Z0-9_.]+)\//);
    if (!m) m = out.match(/(?:ResumedActivity|topResumedActivity)[^\n]*\s([a-zA-Z0-9_.]+)\//);
    return m ? m[1] : '';
  }

  // Tetingkap fokus penuh (pakej/activity). Jalan walau skrin beranimasi, jadi boleh
  // jadi bukti "skrin dah bertukar". '' = tak dapat baca.
  async getFocusedWindow() {
    this._ensure();
    let out = '';
    try { out = await tamatDalam(this.adb.subprocess.noneProtocol.spawnWaitText(['dumpsys', 'window']), 15000, 'Baca skrin fokus'); } catch { return ''; }
    const pick = (re) => { const m = out.match(re); return m ? m[0].trim() : ''; };
    return pick(/mCurrentFocus=[^\n]*/) || pick(/mFocusedApp=[^\n]*/);
  }

  // Taip teks ke medan fokus semasa. Ruang → %s (keperluan `input text`).
  // ⚠️ Had: aksara khas (&, <, (, ), emoji) mungkin tak menaip betul via `input text`.
  inputText(text) {
    const s = String(text == null ? '' : text).replace(/[\r\n\t]+/g, ' ');
    const enc = s.replace(/ /g, '%s');
    return this._run(['input', 'text', enc], `Input text (${s.length} aksara)`);
  }

  // Hantar fail video (Blob) ke telefon melalui ADB sync. onProgress(sent, total).
  async pushVideo(blob, destPath, onProgress) {
    this._ensure();
    if (!blob || !blob.size) throw new AdbError('EMPTY_VIDEO', 'Fail video kosong.');
    const total = blob.size;
    let sent = 0;
    const counter = new TransformStream({
      transform(chunk, controller) {
        sent += chunk?.byteLength || 0;
        if (onProgress) onProgress(sent, total);
        controller.enqueue(chunk);
      },
    });
    const sync = await this.adb.sync();
    try {
      await sync.write({
        filename: destPath,
        file: blob.stream().pipeThrough(counter),
        permission: 0o644,
        mtime: Math.floor(Date.now() / 1000),
      });
    } catch (e) {
      throw new AdbError('PUSH_FAILED', `Gagal memindah video ke telefon: ${e?.message || e}`);
    } finally {
      try { await sync.dispose(); } catch { /* abaikan */ }
    }
    this.log(`Video dipindah ke ${destPath} (${(total / 1048576).toFixed(1)} MB)`, 'ok');
    return total;
  }

  // Suruh Android imbas fail supaya muncul dalam galeri (hasil berbeza ikut peranti).
  async mediaScan(path) {
    this._ensure();
    let out = '';
    try {
      out = (await this.adb.subprocess.noneProtocol.spawnWaitText(
        ['am', 'broadcast', '-a', 'android.intent.action.MEDIA_SCANNER_SCAN_FILE', '-d', `file://${path}`],
      )).trim();
    } catch (e) {
      this.log(`Media scan gagal dihantar: ${e?.message || e}`, 'warn');
      return '';
    }
    this.log(`Media scan dihantar untuk ${path}${out ? ' — ' + (out.split(/\r?\n/)[0] || '') : ''}`);
    return out;
  }

  // Nama fail video paling baru dalam galeri Android ('' = tak dapat baca).
  async newestVideoName() {
    this._ensure();
    let out = '';
    try {
      out = await tamatDalam(this.adb.subprocess.noneProtocol.spawnWaitText(
        'content query --uri content://media/external/video/media --projection _data --sort "date_added DESC" | head -1',
      ), 15000, 'Baca galeri');
    } catch { return ''; }
    const m = out.match(/_data=(.+?)\s*$/m);
    return m ? m[1].split('/').pop() : '';
  }

  // Saiz fail di telefon (bait) — untuk sahkan pemindahan (0 = tiada / gagal).
  async fileSize(path) {
    this._ensure();
    let out;
    try {
      out = (await this.adb.subprocess.noneProtocol.spawnWaitText(['stat', '-c', '%s', path])).trim();
    } catch (e) {
      throw new AdbError('STAT_FAILED', `Tak dapat menyemak fail di telefon: ${e?.message || e}`);
    }
    const n = Number(out);
    if (!Number.isFinite(n)) {
      throw new AdbError('STAT_FAILED', `Fail tidak dijumpai di telefon (${out || 'tiada output'}).`);
    }
    return n;
  }

  // Tangkap susunan UI skrin semasa (uiautomator) → pulang XML mentah.
  // Sumber selector untuk automation (resource-id / text / content-desc / bounds).
  async dumpUi() {
    this._ensure();
    const tmp = '/sdcard/window_dump.xml';
    let out = '';
    // Padam fail lama dulu: dump gagal tanpa mesej ralat tak boleh baca skrin LAMA.
    try { await this.adb.subprocess.noneProtocol.spawnWaitText(['rm', '-f', tmp]); } catch { /* biar */ }
    try {
      out = (await tamatDalam(this.adb.subprocess.noneProtocol.spawnWaitText(['uiautomator', 'dump', tmp]), 30000, 'Baca skrin')).trim();
    } catch (e) {
      throw new AdbError('DUMP_FAILED', 'uiautomator dump gagal: ' + (e?.message || e));
    }
    if (!/dumped to/i.test(out) && /ERROR|could not|null root|No supported|Exception/i.test(out)) {
      throw new AdbError('DUMP_FAILED',
        `Tak dapat membaca susunan skrin ini. (${out.split(/\r?\n/)[0] || ''}) — sesetengah skrin (contoh skrin sensitif) menghalang inspeksi.`);
    }
    let xml;
    try {
      xml = await tamatDalam(this.adb.subprocess.noneProtocol.spawnWaitText(['cat', tmp]), 15000, 'Baca skrin');
    } catch (e) {
      throw new AdbError('DUMP_READ_FAILED', 'Tak dapat membaca fail dump: ' + (e?.message || e));
    }
    if (!xml || !/<hierarchy|<node/i.test(xml)) {
      throw new AdbError('DUMP_EMPTY', 'Dump UI kosong atau tidak sah untuk skrin ini.');
    }
    this.log('UI hierarchy ditangkap.');
    return xml;
  }

  // Dump + parse → senarai elemen (untuk replay selector). Boleh throw bila skrin
  // beranimasi (uiautomator gagal) — pemanggil tangani (replay → pause/needs_review).
  async dumpUiElements() {
    return parseUiXml(await this.dumpUi());
  }

  async disconnect() {
    this._closed = true;
    try { if (this.adb) await this.adb.close(); } catch { /* abaikan */ }
    this.adb = null;
    this.transport = null;
    this.device = null;
    this.info = null;
    this.log('Diputuskan.', 'ok');
  }
}
